import { useEffect, useState } from 'react';
import { color } from '../tokens';
import { AccentButton, Button, TextInput } from '../ui/primitives';
import { ApiError, api } from '../lib/api';
import { useSession } from '../lib/session';
import { postAuthRoute } from '../lib/pendingInvite';
import type { Navigate } from '../lib/router';

/** Sign in / create account. */

export function AuthScreen({ mode, accent, navigate }: { mode: 'in' | 'up'; accent: string; navigate: Navigate }) {
  const setUser = useSession((s) => s.setUser);
  const sso = useSession((s) => s.sso);
  const allowSignup = useSession((s) => s.allowSignup);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Surface the reason an OAuth round trip bounced back here.
  useEffect(() => {
    const reason = new URLSearchParams(location.search).get('error');
    if (reason) {
      setError(reason);
      history.replaceState(null, '', location.pathname);
    }
  }, []);

  const signingUp = mode === 'up';

  const submit = async () => {
    if (busy) return;
    setError(null);

    if (!email.trim() || !password) {
      setError('Enter your email and password.');
      return;
    }
    if (signingUp && password.length < 10) {
      setError('Passwords need at least 10 characters.');
      return;
    }

    setBusy(true);
    try {
      const { user } = signingUp
        ? await api.signup(email, password, name.trim() || undefined)
        : await api.login(email, password);
      setUser(user);
      navigate(postAuthRoute(), { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        overflowY: 'auto',
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        style={{
          width: 372,
          background: color.surface,
          border: `1px solid ${color.lineSoft}`,
          borderRadius: 18,
          padding: '30px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 18, height: 18, borderRadius: 5, background: accent }} />
          <div style={{ fontSize: 14, fontWeight: 700 }}>Scribbleplot</div>
        </div>

        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{signingUp ? 'Create your account' : 'Welcome back'}</div>
          <div style={{ fontSize: 13, color: color.textMuted, marginTop: 4 }}>
            {signingUp ? 'Free while in beta — no card needed.' : 'Sign in to your boards.'}
          </div>
        </div>

        {sso.enabled && (
          <>
            <Button
              onClick={() => {
                // Full page navigation: the sign-in flow leaves the SPA.
                location.href = '/api/auth/oidc';
              }}
              hoverStyle={{ background: '#282932' }}
              style={{
                height: 38,
                borderRadius: 10,
                border: `1px solid rgba(255,255,255,.14)`,
                background: '#22232A',
                color: color.text,
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 9,
              }}
            >
              {sso.name === 'Google' ? (
                <svg width={15} height={15} viewBox="0 0 48 48" aria-hidden>
                  <path
                    fill="#EA4335"
                    d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.7 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.4 17.7 9.5 24 9.5z"
                  />
                  <path
                    fill="#4285F4"
                    d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.7 6C44.2 38 46.5 31.8 46.5 24.5z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M10.5 28.6c-.5-1.5-.8-3-.8-4.6s.3-3.1.8-4.6l-7.9-6.2C.9 16.5 0 20.1 0 24s.9 7.5 2.6 10.8z"
                  />
                  <path
                    fill="#34A853"
                    d="M24 48c6.2 0 11.4-2 15.2-5.6l-7.7-6c-2.1 1.4-4.8 2.3-7.5 2.3-6.3 0-11.6-3.9-13.5-9.4l-7.9 6.2C6.5 42.6 14.6 48 24 48z"
                  />
                </svg>
              ) : (
                // A neutral key glyph stands in for any other issuer.
                <svg width={15} height={15} viewBox="0 0 16 16" aria-hidden>
                  <path
                    d="M9.6 6.4a3 3 0 1 0-2.2 2.2L9 10l1.2-.2.2 1.2 1.3.2.2 1.3 1.6.2.5-.5V10z"
                    style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 1.3, strokeLinejoin: 'round' }}
                  />
                </svg>
              )}
              {sso.name === 'Google' && !signingUp ? 'Sign in with Google' : `Continue with ${sso.name}`}
            </Button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: color.textGhost, fontSize: 11 }}>
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.08)' }} />
              or
              <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.08)' }} />
            </div>
          </>
        )}

        {signingUp && (
          <TextInput value={name} onChange={setName} placeholder="Your name" name="name" autoComplete="name" />
        )}
        <TextInput
          value={email}
          onChange={setEmail}
          placeholder="you@company.com"
          type="email"
          name="email"
          autoComplete="email"
          autoFocus
        />
        <TextInput
          value={password}
          onChange={setPassword}
          placeholder={signingUp ? 'Password — 10 characters or more' : 'Password'}
          type="password"
          name="password"
          autoComplete={signingUp ? 'new-password' : 'current-password'}
        />

        {error && (
          <div role="alert" style={{ fontSize: 12.5, color: color.danger, lineHeight: 1.5 }}>
            {error}
          </div>
        )}

        <AccentButton
          accent={accent}
          type="submit"
          disabled={busy}
          style={{ height: 38, width: '100%', borderRadius: 10, fontSize: 13.5 }}
        >
          {busy ? 'One moment…' : signingUp ? 'Create account' : 'Sign in'}
        </AccentButton>

        {!signingUp && (
          <a
            href="/forgot"
            onClick={(event) => {
              event.preventDefault();
              navigate({ name: 'forgot' });
            }}
            style={{ fontSize: 12, textAlign: 'center', cursor: 'pointer' }}
          >
            Forgot your password?
          </a>
        )}

        <a href="/privacy" style={{ fontSize: 12, textAlign: 'center', color: color.textMuted }}>
          Privacy
        </a>

        <div style={{ fontSize: 12, color: color.textDim, textAlign: 'center' }}>
          {signingUp ? 'Already have an account? ' : 'New here? '}
          {signingUp || allowSignup ? (
            <a
              href={signingUp ? '/signin' : '/signup'}
              onClick={(event) => {
                event.preventDefault();
                setError(null);
                navigate({ name: signingUp ? 'signin' : 'signup' });
              }}
              style={{ cursor: 'pointer' }}
            >
              {signingUp ? 'Sign in' : 'Create an account'}
            </a>
          ) : (
            <span style={{ color: color.textGhost }}>Sign-ups are closed on this instance.</span>
          )}
        </div>

        <a
          href="/"
          onClick={(event) => {
            event.preventDefault();
            navigate({ name: 'landing' });
          }}
          style={{
            fontSize: 12,
            textAlign: 'center',
            cursor: 'pointer',
            color: color.textFaint,
            textDecoration: 'none',
          }}
        >
          ← back to scribbleplot.com
        </a>
      </form>
    </div>
  );
}
